<template>
  <div
    :id="`assessment-details-${details.checklistId}`"
    class="tw-shadow-[0px_0px_4px_0px_rgba(0,0,0,0.05)] tw-rounded-[6px]"
  >
    <div
      v-if="isLoading"
      class="custom-global-skeleton tw-w-full tw-h-[68px] tw-rounded-[6px]"
    ></div>
    <div
      v-else
      :class="` tw-flex-wrap tw-cursor-pointer tw-select-none tw-pr-[25px] tw-bg-[#FCFCFD] tw-w-full tw-flex tw-flex-row tw-justify-between tw-items-center tw-self-stretch tw-border-[1px] tw-border-b-[1px] tw-border-solid tw-border-[#EFF0F1] ${
        showContent ? 'tw-rounded-t-[6px]' : 'tw-rounded-[6px]'
      }`"
      @click.self="collapseDetails"
    >
      <div class="tw-flex tw-flex-row tw-justify-start tw-items-center">
        <div class="tw-py-[20px]" @click="handleSelectAll">
          <CustomCheckBox
            :id="`assessment-details-select-all-radio-${details.checklistId}`"
            v-model:is-checked="selectAll"
            class="tw-h-[20px] tw-ml-[25px] !tw-mb-0"
            size="20px"
            @update:is-checked="handleSelectAssessment"
          />
        </div>
        <div class="tw-ml-[-5px]">
          <input
            v-if="isUpdateAssessmentName"
            :id="`assessment-details-rename-input-${details.checklistId}`"
            v-model="assessmentName"
            type="text"
            class="tw-rounded-md tw-w-[200px] tw-border tw-border-solid tw-border-[#EFF0F1] tw-py-[5px] tw-px-[10px] tw-text-[15px] tw-font-[400] tw-text-[#211F58] tw-mr-2"
            @keyup.enter="onRenameAssessment"
            @blur="onRenameAssessment"
          />
          <span
            v-if="!isUpdateAssessmentName"
            data-cy="assessment-details-name"
            class="tw-font-Poppins tw-text-[18px] tw-font-[600] tw-text-[#211F58]"
            >{{ assessmentName }} -
          </span>
          <span
            v-if="details.checklistStatusDate"
            class="tw-font-Poppins tw-text-[18px] tw-font-[400] tw-text-[#211F58]"
          >
            {{
              convertUTCTimeToLocalTime(
                details.checklistDateReporting
                  ? details.checklistDateReporting
                  : details.checklistStatusDate,
              )
            }}
          </span>
        </div>
      </div>
      <div class="tw-flex tw-items-center">
        <div
          class="tw-flex tw-items-center sm:tw-flex-row sm:tw-py-0 tw-flex-col tw-py-[4px] tw-ml-[7px]"
        >
          <div class="tw-flex tw-flex-col tw-items-center tw-relative">
            <div
              v-if="contributorNameOnHover"
              class="tw-absolute tw-bottom-full tw-whitespace-nowrap tw-bg-[#211F58] tw-text-[11px] tw-font-medium tw-rounded tw-text-[#FFFFFF] tw-px-[5px] tw-py-[3px] tw-ml-[-4px] tw-w-fit"
            >
              <span>{{ contributorNameOnHover }}</span>
            </div>
            <AvatarStack
              :avatars="listAvatar"
              :avatar-size="32"
              :max-shown="2"
              @on:hover="onHoverAvatar"
            />
          </div>
          <p class="tw-mb-0 sm:tw-ml-[20px] tw-ml-0 tw-font-Poppins">
            <span
              class="tw-text-[#878787] tw-text-[15px] tw-font-[400] tw-leading-[100%]"
            >
              {{ percentScore }}%
            </span>
            <span
              :class="[
                'sm:tw-ml-[5px] tw-ml-0 tw-text-[15px] tw-font-[600] tw-leading-[100%]',
                generateGradeColorScoreWithCompletedScore(Number(percentScore)),
              ]"
            >
              {{ generateGradeScoreWithCompletedScore(Number(percentScore)) }}
            </span>
          </p>
        </div>
        <PopoverAssessment
          class="tw-h-[56px]"
          :offset="0"
          :id-popover="idPopover"
          style-button="tw-w-[61px] tw-items-center tw-justify-center"
          b-popover="popover-has-tag"
        >
          <CustomDropDownItem
            v-for="item in subMenuOptions"
            :id="item.textId"
            :key="`option_detail_${item.id}`"
            :is-show-option="item.isShow"
            :data-cy-id="item.dataCy"
            :id-text="`${item.text}`"
            :custom-text-class="`tw-flex tw-items-center tw-gap-[5px] tw-w-[90%] !tw-text-[13px] !tw-font-normal !tw-leading-[19.5px] ${
              item.arrowRed ? 'tw-text-[#F7553C]' : ''
            } tw-whitespace-normal`"
            :arrow-icon-style="`${
              item.arrowRed ? 'tw-text-[#F7553C]' : 'tw-text-[#387DFF]'
            } tw-min-w-[7px]`"
            :padding="'tw-px-[13px] tw-py-[12px]'"
            :custom-class="'tw-flex tw-justify-between tw-items-center !tw-border-0'"
            :tag-status="item.statusTag"
            :tag-status-id="item.statusTagId"
            @click="item.action(details)"
          />
        </PopoverAssessment>
        <span
          :id="`assessment-details-collapse-${details.checklistId}`"
          class="float-right tw-p-[10px] tw-ml-[-10px] tw-mr-[-10px]"
          @click="collapseDetails"
        >
          <ArrowIcon
            :class="['right-arrow tw-mr-[2px]', showContent ? 'show-all' : '']"
            :width="6"
            :height="9"
          />
        </span>
      </div>
    </div>

    <!-- Content -->
    <div
      :class="[
        'content tw-flex tw-flex-col tw-gap-[20px] tw-bg-[#FFFFFF] tw-rounded-b-[6px] tw-border-0 tw-border-solid tw-border-[#EFF0F1]',
        showContent
          ? 'show tw-h-auto tw-py-[25px] tw-px-[20px] tw-border-[1px] tw-border-t-0'
          : '',
      ]"
    >
      <div class="card-container tw-flex tw-gap-[25px] tw-flex-row">
        <div
          v-if="isLoading"
          class="custom-global-skeleton tw-w-full tw-max-w-[558px] tw-h-[112px] tw-rounded-[6px]"
        ></div>
        <CompletionCard
          v-else
          :class="['card-content tw-w-[45%] tw-grow']"
          :class-style-content="'!tw-min-w-[245px] !tw-max-w-[445px] !tw-bg-[#FCFCFD]'"
          :class-style-icon="'!tw-bg-[#FCFCFD]'"
          :detail="details"
          :status-detail="checklistStatusDetail"
          :checklist-completion-props="checklistCompletion"
          :checklist-score-detail="checklistScoreDetail"
          :is-assessment-detail="true"
        />
        <div
          v-if="isLoading"
          class="custom-global-skeleton tw-w-full tw-max-w-[558px] tw-h-[112px] tw-rounded-[6px]"
        ></div>
        <DetailsCard
          v-else
          :class="'card-content tw-w-[45%] tw-max-w-[558px] tw-grow !tw-bg-[#FCFCFD]'"
          :is-assessment-detail="true"
          :detail="details"
        />
      </div>

      <!-- Description -->
      <div
        v-if="isLoading"
        class="custom-global-skeleton tw-w-full tw-h-[74.5px] tw-rounded-[6px]"
      ></div>
      <div
        v-else
        class="tw-p-[20px] tw-bg-[#FCFCFD] tw-border tw-border-solid tw-border-[#EFF0F1] tw-rounded-[6px]"
      >
        <p
          class="tw-text-[15px] tw-font-[600] tw-text-[#211F58] tw-capitalize tw-mb-[10px]"
        >
          <span>{{
            getComponentUiText(
              "app-component-assessment-detail-description-title-1",
            )
          }}</span>
        </p>
        <p
          v-for="(line, idx) in assessmentDecriptionStore.split('\n')"
          :key="idx"
          class="tw-text-[13px] tw-font-[400] tw-text-[#878787] tw-mb-0 tw-block"
        >
          {{ line }}
          <br />
        </p>
      </div>
      <!-- History -->
      <div
        v-if="isLoading"
        class="custom-global-skeleton tw-w-full tw-h-[74.5px] tw-rounded-[6px]"
      ></div>
      <AssessmentHistory
        v-else
        :index="index"
        :assessments="details.checklistItem"
        :assessment-name="assessmentNameStore"
        :checklist-id="checklistId"
        @on-refresh="handleRefresh"
      />

      <!-- Group Button -->
      <div class="tw-w-full tw-flex tw-justify-between">
        <div class="tw-flex">
          <b-button
            variant="primary"
            data-cy="assessment-detail-duplicate-assessment"
            class="button-verify-later tw-text-[#387DFF] tw-bg-[#FFFFFF] tw-py-[8px] tw-mr-[10px] tw-px-[20px] tw-text-[13px] tw-font-medium tw-rounded-[100px]"
            @click="handleDuplicateAssessment"
          >
            {{
              getComponentUiText(
                "app-component-assessment-detail-button-duplicate-1",
              )
            }}
          </b-button>
          <ButtonShowDuplicateAssessment
            :id-popover="idPopoverShowDuplicateAssessment"
            :custom-class="'tw-max-w-[470px]'"
            :classes="`tw-leading-[36px]`"
          >
            <div class="tw-p-[25px]">
              <div class="tw-text-[14px] tw-font-[500] tw-text-[#211F58]">
                {{
                  getComponentUiText(
                    "app-component-assessment-title-duplicate-1",
                  )
                }}
              </div>
              <div
                class="tw-py-[10px] tw-text-[13px] tw-font-[400] tw-text-[#231F20] tw-text"
              >
                {{
                  getComponentUiText(
                    "app-component-assessment-content-duplicate-1",
                  )
                }}
              </div>
              <div
                class="tw-text-[13px] tw-font-[400] tw-text-[#231F20] tw-text"
              >
                {{
                  getComponentUiText(
                    "app-component-assessment-content-process-1",
                  )
                }}
              </div>
            </div>
          </ButtonShowDuplicateAssessment>
        </div>
        <b-button
          variant="primary"
          class="button-verify tw-bg-[#387DFF] tw-ml-[10px] tw-py-[8px] tw-px-[20px] tw-text-[13px] tw-font-medium tw-rounded-[100px]"
          type="submit"
          @click="rejectAssessment(details)"
        >
          {{
            checklistStatusDetail.checklistStatusId !==
            checklistStatusId.completed
              ? getComponentUiText(
                  "app-component-assessment-detail-button-continue-assessment-1",
                )
              : getComponentUiText(
                  "app-component-assessment-detail-dropdown-option-view-assessment-title-1",
                )
          }}
        </b-button>
      </div>
    </div>
    <CreateAndUpdateAssessmentModal
      v-model:show="createAndUpdateAssessmentShow"
      :checklist-id="checklistId"
    />
  </div>
</template>

<script setup lang="ts">
import {
  ComputedRef,
  PropType,
  Ref,
  computed,
  onMounted,
  ref,
  watch,
} from "vue";
import CustomCheckBox from "@/components/common/customCheckBox.vue";
import {
  IAssessmentDetail,
  IChecklistMetricsCompletion,
  IChecklistScoreDetail,
} from "@/interfaces/assessments/IAssessmentDetails";
import ArrowIcon from "../svgs/arrowIcon.vue";
import CompletionCard from "../assessments/completionCard.vue";
import DetailsCard from "../assessments/detailsCard.vue";
import {
  downloadCSV,
  getComponentUiText,
  getFullnamFromFirstAndLastName,
} from "@/utils/utilities";
import {
  checklistStatusId,
  successStatusCodes,
  checklistStatusNotes,
  MODAL_NAME,
  CHECKLISTSTATUS,
} from "@/utils/constants";
import { convertUTCTimeToLocalTime } from "@/utils/dateTimeUtils";
import AvatarStack from "../common/avatarStack.vue";
import AssessmentHistory from "./assessmentHistory.vue";
import router from "@/router";
import store from "@/store";
import {
  deleteChecklist,
  generateGradeColorScoreWithCompletedScore,
  generateGradeScoreWithCompletedScore,
  getCheckListDuplicate,
  getCheckListIdMetricsCompletion,
  getCheckListStatusDetails,
  getChecklistContributors,
  putChecklistStatus,
  getChecklist,
} from "@/services/assessment";
import EventEmitter, { EmitterEvents } from "@/utils/eventEmitter";
import { IToastMessage, ToastVariant } from "@/interfaces/common/IToastMessage";
import { IContributor } from "@/interfaces/assessments/IContributors";
import { Assessment, Checklist, UserRoute } from "@/services/apiRoutes/routes";
import { showErrorAPI, showSuccessToast } from "@/services/apiRoutes";
import get from "lodash/get";
import { IUserProfile, IUserProfileImage } from "@/interfaces/userInterfaces";
import { IApiPromise } from "@/interfaces/responseData";
import userService from "@/services/user/userService";
import ButtonShowDuplicateAssessment from "../common/setting/buttonShowDuplicateAssessment.vue";
import PopoverAssessment from "./popoverAssessment.vue";
import CreateAndUpdateAssessmentModal from "./createAndUpdateAssessmentModal.vue";
import { IModalMessage } from "@/interfaces/common/common";
import { getChecklistCSV } from "@/services/assessment";
import dayjs from "dayjs";
import CustomDropDownItem from "../common/customDropDownItem.vue";

const SCORE_DETAIL_DEFAULT = {
  checklistScore: 0,
  checklistScoreMax: 100,
  checklistScorePcnt: 0,
} as IChecklistScoreDetail;

const emit = defineEmits(["select:assessment", "loaded", "onRefresh"]);
const props = defineProps({
  details: { type: Object as PropType<IAssessmentDetail>, required: true },
  checklistId: { type: Number, required: true },
  showDetails: { type: Boolean, required: false, default: false },
  idPopover: { type: String, required: false, default: "" },
  idPopoverShowDuplicateAssessment: {
    type: String,
    required: false,
    default: "",
  },
  index: { type: Number, required: false, default: 0 },
});
const createAndUpdateAssessmentShow = ref(false);
const percentScore = computed(() =>
  (checklistScoreDetail.value.checklistScorePcnt * 100).toFixed(1),
);

const scoreDetail: ComputedRef<IChecklistScoreDetail> = computed(() => {
  let detail = SCORE_DETAIL_DEFAULT as IChecklistScoreDetail;
  const data: IAssessmentDetail[] =
    store.getters["Assessment/getChecklistAssessment"];
  const checklistData = data.find((e) => e.checklistId === props.checklistId);
  if (checklistData) detail = checklistData.scoreDetail || SCORE_DETAIL_DEFAULT;
  return detail;
});

const isScoreDetailsLoading: ComputedRef<boolean> = computed(() => {
  return (
    JSON.stringify(scoreDetail.value) === JSON.stringify(SCORE_DETAIL_DEFAULT)
  );
});
const selectAll: Ref<boolean> = ref(false);
const showContent: Ref<boolean> = ref(false);
const listAvatar: Ref<IUserProfileImage[]> = ref([]);
const isListAvatarLoading: Ref<boolean> = ref(true);
const checklistId = ref(props.details.checklistId);
const currentUser = computed(() => store.state.User.userProfile);

const checklistStatusDetail = computed(() => {
  const data = store.getters["Assessment/getChecklistAssessment"];
  const checklist = data.find((e) => e.checklistId === props.checklistId);
  return {
    checklistStatusId:
      checklist?.checklistStatusId || checklistStatusId.inProgress,
    checklistStatus: checklist?.checklistStatus || "",
  };
});

interface Option {
  id: number;
  textId: string;
  dataCy: string;
  text: string;
  arrowRed: boolean;
  statusTag: string;
  statusTagId: string;
  isShow: boolean;
  action: (item: any) => void;
}
const subMenuOptions: Ref<Option[]> = computed(() => [
  {
    id: 1,
    textId: `assessment-details-popover-item-pause-${props.details.checklistId}`,
    dataCy: "assessment-details-pause-assessment",
    text: "app-component-dropdown-set-assessment-to-1",
    arrowRed: false,
    statusTag: CHECKLISTSTATUS.ONHOLD,
    statusTagId: CHECKLISTSTATUS.PAUS,
    isShow:
      checklistStatusDetail.value.checklistStatusId !==
        checklistStatusId.completed &&
      checklistStatusDetail.value.checklistStatusId !==
        checklistStatusId.paused &&
      checklistStatusDetail.value.checklistStatusId !==
        checklistStatusId.defunct,
    action: () => handlePutOnHold(),
  },
  {
    id: 2,
    textId: `assessment-details-popover-item-set-assessment-inprog-${props.details.checklistId}`,
    dataCy: "assessment-details-take-off-hold",
    text: "app-component-dropdown-set-assessment-to-1",
    arrowRed: false,
    statusTag: CHECKLISTSTATUS.INPROG,
    statusTagId: CHECKLISTSTATUS.INPROG,
    isShow:
      checklistStatusDetail.value.checklistStatusId ===
      checklistStatusId.paused,
    action: () => handleTakeOffHold(),
  },
  {
    id: 3,
    textId: `assessment-details-popover-item-set-assessment-complete-${props.details.checklistId}`,
    dataCy: "assessment-details-complete-assessment",
    text: "app-component-dropdown-set-assessment-to-1",
    arrowRed: false,
    statusTag: CHECKLISTSTATUS.COMPLETED,
    statusTagId: CHECKLISTSTATUS.DONE,
    isShow:
      checklistStatusDetail.value.checklistStatusId !==
        checklistStatusId.defunct &&
      checklistStatusDetail.value.checklistStatusId !==
        checklistStatusId.completed,
    action: () => handleSetToComplete(),
  },
  {
    id: 4,
    textId: `assessment-details-popover-item-download-${props.details.checklistId}`,
    dataCy: "assessment-details-download-csv",
    text: "app-component-assessment-detail-dropdown-option-download-assessment-csv-title-1",
    arrowRed: false,
    statusTag: "",
    statusTagId: "",
    isShow: true,
    action: () => handleDownloadAssessmentCSV(),
  },
  {
    id: 5,
    textId: `assessment-details-popover-item-rename-${props.details.checklistId}`,
    dataCy: "assessment-details-rename-assessment",
    text: "app-assess-assessment-history-rename-assessment-1",
    arrowRed: false,
    statusTag: "",
    statusTagId: "",
    isShow: false,
    action: () => handleRenameAssessment(),
  },
  {
    id: 6,
    textId: `assessment-details-popover-item-update-des-${props.details.checklistId}`,
    dataCy: "assessment-details-update-description",
    text: "app-assess-assessment-history-update-description-1",
    arrowRed: false,
    statusTag: "",
    statusTagId: "",
    isShow: false,
  },
  {
    id: 11,
    textId: `assessment-details-popover-item-update-assessment-details-${props.details.checklistId}`,
    dataCy: "assessment-details-update-description",
    text: "app-component-assessment-update-assessment-detail-modal-title-1",
    arrowRed: false,
    statusTag: "",
    statusTagId: "",
    isShow:
      checklistStatusDetail.value.checklistStatusId !==
        checklistStatusId.defunct &&
      checklistStatusDetail.value.checklistStatusId !==
        checklistStatusId.completed,
    action: () => handleUpdateAssessmentDetails(),
  },
  {
    id: 7,
    textId: `assessment-details-popover-item-history-inprog-${props.details.checklistId}`,
    dataCy: "assessment-details-progress-assessment",
    text: "app-component-dropdown-set-assessment-to-1",
    arrowRed: false,
    statusTag: CHECKLISTSTATUS.INPROG,
    statusTagId: CHECKLISTSTATUS.INPROG,
    isShow:
      checklistStatusDetail.value.checklistStatusId ===
        checklistStatusId.completed ||
      checklistStatusDetail.value.checklistStatusId ===
        checklistStatusId.defunct,
    action: () => handleUpdateToInprogress(),
  },
  {
    id: 8,
    textId: `assessment-details-popover-item-history-create-${props.details.checklistId}`,
    dataCy: "assessment-details-update-template",
    text: "app-assess-assessment-history-create-or-update-assessment-template-1",
    arrowRed: false,
    statusTag: "",
    statusTagId: "",
    isShow: true,
    action: () => handleCreateAndUpdateAssessment(),
  },
  {
    id: 9,
    textId: `assessment-details-popover-item-history-delete-${props.details.checklistId}`,
    dataCy: "assessment-details-delete-assessment",
    text: "app-assess-assessment-history-delete-assessment-1",
    arrowRed: true,
    statusTag: "",
    statusTagId: "",
    isShow:
      checklistStatusDetail.value.checklistStatusId !==
        checklistStatusId.defunct &&
      checklistStatusDetail.value.checklistStatusId !==
        checklistStatusId.completed,
    action: () => handleDeleteAssessment(),
  },
  {
    id: 10,
    textId: `assessment-details-popover-item-history-permanently-${props.details.checklistId}`,
    dataCy: "assessment-details-permanently-assessment",
    text: "app-assess-assessment-history-permanently-delete-assessment-1",
    arrowRed: true,
    statusTag: "",
    statusTagId: "",
    isShow:
      checklistStatusDetail.value.checklistStatusId ===
      checklistStatusId.defunct,
    action: () => handleDeleteAssessmentPermanently(),
  },
]);

const checklistCompletion = ref({} as IChecklistMetricsCompletion);
const checklistScoreDetail: Ref<IChecklistScoreDetail> = computed(
  () =>
    store.getters["Assessment/getScoreDetail"](props.checklistId) ??
    SCORE_DETAIL_DEFAULT,
);
const assessmentNameStore: Ref<string> = computed(() => {
  const data = store.getters["Assessment/getChecklistAssessment"];

  const checklist = data.find((e) => e.checklistId === props.checklistId);

  return checklist?.checklistName;
});
const assessmentDecriptionStore: Ref<string> = computed(() => {
  const data = store.getters["Assessment/getChecklistAssessment"];

  const checklist = data.find((e) => e.checklistId === props.checklistId);

  return checklist?.checklistDescription ?? "";
});
const assessmentName = ref(props.details.checklistName);
const isUpdateAssessmentName: Ref<boolean> = ref(false);
const isCallApiUpdate = ref(false);

watch(
  () => assessmentNameStore.value,
  (newVal) => {
    assessmentName.value = newVal;
  },
);

onMounted(() => initial());

const initial = async () => {
  try {
    isListAvatarLoading.value = true;
    await Promise.all([getImageData(), getDataStatusCard()]);
    isListAvatarLoading.value = false;
  } catch (error) {
    showErrorAPI(error, Assessment.getChecklistContributors);
  }
};

const contributors: Ref<any[]> = ref([]);
const contributorNameOnHover: Ref<string> = ref("");
const listProfile: Ref<any[]> = ref([]);
const scoreDetails: Ref<IAssessmentDetail[]> = computed(
  () => store.state.Assessment.scoreDetail,
);

const isLoading: ComputedRef<boolean> = computed(
  () =>
    isListAvatarLoading.value ||
    isScoreDetailsLoading.value ||
    scoreDetails.value.length === 0,
);

watch(
  () => isLoading.value,
  (newVal) => {
    if (newVal === false) emit("loaded");
  },
);

const getImageData = async () => {
  await getListContributor(checklistId.value);
  const userIds = contributors.value.map((c) => c.userId);
  await getUserProfile(userIds);
  // Get list image.
  const avatarImageFileIds = listProfile.value.map(
    (p) => p.avatar.avatarImageFileId,
  );
  listAvatar.value = await getListImageUser(avatarImageFileIds);

  // Add default image if user have not.
  processData();
};

const getListContributor = async (checklistId: number) => {
  if (!checklistId || checklistId <= 0) contributors.value = [];

  try {
    const res = await getChecklistContributors(checklistId);
    if (res.status !== successStatusCodes.OK) contributors.value = [];

    const list = get(res, "data.data.contributors", []) as IContributor[];
    contributors.value = list;
  } catch (error) {
    showErrorAPI(error, Assessment.getChecklistContributors);
    contributors.value = [] as IContributor[];
  }
};

const getUserProfile = async (userIds: string[]) => {
  try {
    const listResult: IUserProfile[] = [];
    const promise: IApiPromise<IUserProfile>[] = [];
    userIds.map((u) => {
      if (u === currentUser.value.userId) {
        listResult.push(currentUser.value);
      } else promise.push(userService.getUserProfile(u));
    });

    const resArray = await Promise.all(promise);
    resArray.forEach((res) =>
      listResult.push(get(res, "data.data", {} as IUserProfile)),
    );
    listProfile.value = listResult;
  } catch (error) {
    showErrorAPI(error, UserRoute.getUserProfile);
    listProfile.value = [];
  }
};

const getListImageUser = async (ids: number[]) => {
  const listResult: IUserProfileImage[] = [];
  const promise: IApiPromise<IUserProfileImage>[] = [];
  try {
    ids.forEach(
      (id) =>
        id && promise.push(userService.getUserProfileImage(id.toString())),
    );

    if (promise.length) {
      const listRes = await Promise.all(promise);
      listRes.forEach((r) =>
        listResult.push(get(r, "data.data", {} as IUserProfileImage)),
      );
      return listResult;
    }
  } catch (error) {
    showErrorAPI(error, UserRoute.getUserProfileImage);
    return [];
  }

  return [];
};
const handlePutOnHold = async () => {
  await handlePostChecklistStatus(props.details.checklistId, {
    checklistStatusId: checklistStatusId.paused,
    checklistStatusNotes: checklistStatusNotes.createScreen,
  });
  EventEmitter.emit(EmitterEvents.HIDDEN_SETTINGS);
};

const handleUpdateToInprogress = async () => {
  await handlePostChecklistStatus(props.details.checklistId, {
    checklistStatusId: checklistStatusId.inProgress,
    checklistStatusNotes:
      checklistStatusDetail.value.checklistStatusId ===
      checklistStatusId.completed
        ? checklistStatusNotes.updateFromCompleted
        : checklistStatusNotes.updateFromDefunct,
  });
  EventEmitter.emit(EmitterEvents.HIDDEN_SETTINGS);
};

const handleDeleteAssessment = async () => {
  await handlePostChecklistStatus(props.details.checklistId, {
    checklistStatusId: checklistStatusId.defunct,
    checklistStatusNotes: checklistStatusNotes.updateScreen,
  });
  EventEmitter.emit(EmitterEvents.HIDDEN_SETTINGS);
};

const handleDeleteAssessmentPermanently = async () => {
  EventEmitter.emit(EmitterEvents.HIDDEN_SETTINGS);
  try {
    const res = await deleteChecklist(props.details.checklistId);
    store.dispatch("Recommend/fetchChecklistData");
    if (res.status === successStatusCodes.OK) {
      emit("onRefresh");
    } else {
      showError(res.data.message);
    }
  } catch (error: any) {
    showError(error.message);
  }
};

const handleRefresh = () => {
  emit("onRefresh");
};

const handleCreateAndUpdateAssessment = () => {
  EventEmitter.emit(EmitterEvents.HIDDEN_SETTINGS);
  createAndUpdateAssessmentShow.value = true;
};

const handleDownloadAssessmentCSV = async () => {
  try {
    EventEmitter.emit(EmitterEvents.HIDDEN_SETTINGS);
    const res = await getChecklistCSV(props.details.checklistId);
    downloadCSV(
      res,
      `CyberSystem_Assessment_Export_${dayjs().format("YYYYMMDD_HHmmss")}_${
        assessmentName.value
      }.csv`,
    );
    showSuccessToast(
      getComponentUiText("app-api-scanning-export-csv-success-1"),
    );
  } catch (error) {
    showErrorAPI(error, Checklist.getChecklistCSV);
  }
};

const handleTakeOffHold = async () => {
  EventEmitter.emit(EmitterEvents.HIDDEN_SETTINGS);
  await handlePostChecklistStatus(props.details.checklistId, {
    checklistStatusId: checklistStatusId.inProgress,
    checklistStatusNotes: checklistStatusNotes.updateFromPaused,
  });
};
const processData = () => {
  contributors.value.forEach((c) => {
    const avatar = listAvatar.value.find((a) => a.userId == c.userId);
    if (!avatar) {
      listAvatar.value.push({
        fileName: "",
        userId: c.userId,
        fileSize: 0,
        fileType: "",
        profileImageTypeId: 0,
        file: null,
        userName: getFullnamFromFirstAndLastName(c.firstName, c.lastName),
      });
    }
  });
  if (props.showDetails) showContent.value = true;
};

const getDataStatusCard = async () => {
  try {
    const res = await getCheckListIdMetricsCompletion(`${props.checklistId}`);
    const data = get(res, "data.data", {});
    checklistCompletion.value = data;
    checklistCompletion.value = {
      ...data,
      checklistCompletionPcnt: parseFloat(
        (data.checklistCompletionPcnt * 100).toFixed(2),
      ),
    };
  } catch (error) {
    checklistCompletion.value = {} as IChecklistMetricsCompletion;
    checklistScoreDetail.value = SCORE_DETAIL_DEFAULT;
  }
};

const collapseDetails = (e: any) => {
  if (e.target.id === "non-effective") return;
  if (isUpdateAssessmentName.value) return;
  showContent.value = !showContent.value;
};
const rejectAssessment = (data) => {
  const query = { checklistId: data.checklistItem[0].checklistId };
  router.push({ path: "/assess/input", query });
};
const rejectAssessmentDuplicate = async (data, payload) => {
  const res = await getCheckListDuplicate(data.checklistId, payload);
  if (
    res.data?.data &&
    res.status === successStatusCodes.OK &&
    typeof res.data.data === "object" &&
    "checklistId" in res.data.data
  ) {
    emit("onRefresh");
    showSuccessToast(
      getComponentUiText(
        "app-component-assessment-duplicate-assessment-success-1",
      ),
      getComponentUiText(
        "app-component-assessment-duplicate-assessment-link-1",
      ),
      `/assess/input?checklistId=${res.data.data.checklistId}`,
      8000,
    );
  } else {
    showError(res.data.message);
  }
};
const showError = (message) => {
  const emitData: IToastMessage = {
    variant: ToastVariant.ERROR,
    message,
  };
  EventEmitter.emit(EmitterEvents.APP_LAYOUT_SHOW_TOAST_MESSAGE, emitData);
};
const handleSelectAll = (e: Event) => {
  e.stopPropagation();
  selectAll.value = !selectAll.value;
};

const handleSelectAssessment = () => {
  selectAll.value = !selectAll.value;
  emit("select:assessment", props.details.checklistId);
};

const handlePostChecklistStatus = async (checklistId: number, data: any) => {
  try {
    const res = await putChecklistStatus(checklistId, data);

    if (res.data?.data && res.status === successStatusCodes.OK) {
      Promise.all([
        getChecklist(checklistId),
        getCheckListStatusDetails(checklistId),
      ]).then(([res1, res2]) => {
        //  update data in store
        store.dispatch("Assessment/updateChecklistStatus", {
          checklistId,
          params: { ...res1.data.data, ...res2.data.data },
        });
      });
      getDataStatusCard();
      return true;
    } else {
      showError(res.data?.message);
      return false;
    }
  } catch (error: any) {
    showError(error?.message);
    return false;
  }
};

const handleSetToComplete = async () => {
  EventEmitter.emit(EmitterEvents.HIDDEN_SETTINGS);
  const emitData: IModalMessage = {
    component: MODAL_NAME.MODAL_COMPLETE_ASSESSMENT,
    properties: {
      checklistParentId: props.checklistId,
      updateDataFunction: completeAssessment,
    },
    events: {
      "update:data": completeAssessment,
    },
  };
  EventEmitter.emit(EmitterEvents.OPEN_MODAL, emitData);
};

const completeAssessment = async (value) => {
  const { note, checklistReportingDate } = value;

  const payload: Record<string, any> = {
    checklistStatusId: checklistStatusId.completed,
    checklistStatusNotes: note,
  };

  if (checklistReportingDate) {
    payload.setReportingDate = true;
    payload.checklistReportingDate = checklistReportingDate;
  }

  return await handlePostChecklistStatus(props.details.checklistId, payload);
};

const handleRenameAssessment = async () => {
  EventEmitter.emit(EmitterEvents.HIDDEN_SETTINGS);
  isUpdateAssessmentName.value = true;
  const idName = `assessment-details-rename-input-${props.details.checklistId}`;
  setTimeout(() => {
    document.getElementById(idName)?.focus();
  }, 300);
};

const handleUpdateAssessmentDetails = async () => {
  EventEmitter.emit(EmitterEvents.HIDDEN_SETTINGS);
  try {
    const res = await getChecklist(props.checklistId);
    const checklistAssessmentById = res.data?.data || {};

    const emitData: IModalMessage = {
      component: MODAL_NAME.MODAL_UPDATE_ASSESSMENT_DETAIL,
      properties: {
        checklistAssessmentData: {
          ...checklistAssessmentById,
          checklistDescription: checklistAssessmentById.checklistDescription,
          checklistName: checklistAssessmentById.checklistName,
        },
      },
      events: {
        "update:data": () => {},
      },
    };
    EventEmitter.emit(EmitterEvents.OPEN_MODAL, emitData);
  } catch (error) {
    showErrorAPI(error, Checklist.getChecklist);
  }
};

const onRenameAssessment = async () => {
  isUpdateAssessmentName.value = false;
  if (isCallApiUpdate.value) return;

  isCallApiUpdate.value = true;
  const res = await store.dispatch("Assessment/updateChecklistRename", {
    checklistId: checklistId.value,
    params: {
      checklistName: assessmentName.value,
    },
  });
  setTimeout(() => {
    isCallApiUpdate.value = false;
  }, 100);

  if (!res) {
    assessmentName.value = assessmentNameStore.value;
  }
};

const handleDuplicateAssessment = async () => {
  EventEmitter.emit(EmitterEvents.HIDDEN_SETTINGS);
  const emitData: IModalMessage = {
    component: MODAL_NAME.MODAL_DUPLICATE_ASSESSMENT,
    properties: {
      checklistAssessmentData: {
        ...props.details,
        checklistName: `${props.details?.checklistName} ${getComponentUiText(
          "app-component-assessment-duplicate-assessment-modal-field-suffix-name-1",
        )}`,
        checklistDescription: assessmentDecriptionStore.value,
      },
      updateDataFucntion: async (payload) => {
        await rejectAssessmentDuplicate(props.details, payload);
      },
    },
    events: {
      "update:data": (payload) =>
        rejectAssessmentDuplicate(props.details, payload),
    },
  };
  EventEmitter.emit(EmitterEvents.OPEN_MODAL, emitData);
};

const onHoverAvatar = (image: IUserProfileImage) => {
  if (Object.keys(image).length == 0) contributorNameOnHover.value = "";
  else {
    const contributor = contributors.value.find(
      (x) => x.userId == image.userId,
    );
    if (contributor)
      contributorNameOnHover.value =
        contributor.firstName + " " + contributor.lastName;
  }
};
</script>
<style lang="scss" scoped>
.title__settings {
  font-family: Poppins;
  line-height: 100%; /* 12px */
  letter-spacing: 1.2px;
  text-transform: uppercase;
}
.content__settings {
  font-family: Poppins;
}
.right-arrow {
  transition: transform 0.3s ease;
  transform: rotate(90deg) scale(1.2);

  &.show-all {
    transform: rotate(-90deg) scale(1.2);
  }
}
.arrow-red {
  color: red;
}
.content {
  height: 0px;
  overflow: hidden;
  transition: height 0.3s ease;
  &.show {
    height: auto;
  }
}

@media screen and (max-width: 845px) {
  .card-container {
    flex-direction: column;

    .card-content {
      width: 80%;
    }
  }
}
</style>
